import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { cleanUpObject, combineClassNames, getMediaPath } from 'app/functions';
import * as routes from 'constants/routes';
import Loader from 'components/common/loader';
import CategorySlider from 'components/common/sliders/category_slider';
import SliderArrow from 'components/common/shared/slider_arrow';
import { searchApi } from 'redux/api/search.api';
import searchIcon from 'assets/icons/search-icon-white.svg';
import closeIcon from 'assets/icons/x-close-mobile-icon.svg';
import questionIcon from 'assets/icons/question-icon.svg';
import tipsIcon from 'assets/icons/tips-icon.svg';
import {
    HOT_YOTER_ALL_CATEGORIES_ID,
    HOT_YOTER_ALL_GROUPS_ID,
} from '../constants';
import {
    buildHotYoterAvailableCategories,
    buildHotYoterSearchParams,
    filterActiveHotYoterBenefits,
    getHotYoterCategoryFilterValues,
    getHotYoterSearchMetadata,
    getHotYoterSearchRecords,
    normalizeHotYoterTextQuery,
} from '../utils/buildHotYoterSearchParams';
import { HotYoterBenefitsResults } from './HotYoterBenefitsResults';
import { HotYoterGroupTags } from './HotYoterGroupTags';

// Match category_page filters: debounce typing, then send textQuery to the API.
const SEARCH_REQUEST_DELAY = 1.5 * 1000;

const CATEGORY_SLIDER_SETTINGS = {
    shared: {
        nextArrow: <SliderArrow />,
        prevArrow: <SliderArrow />,
    },
    mobile: {
        slidesToShow: 4,
        slidesToScroll: 4,
    },
    tablet: {
        slidesToShow: 5,
        slidesToScroll: 5,
    },
    desktop: {
        slidesToShow: 5,
        slidesToScroll: 5,
    },
    desktopLarge: {
        slidesToShow: 5,
        slidesToScroll: 5,
    },
};

const getSearchPlaceholder = (
    searchPlaceholderText,
    translations,
    selectedGroupId,
    benefitsGroups,
    pageTitle,
) => {
    if (selectedGroupId !== HOT_YOTER_ALL_GROUPS_ID) {
        const selectedGroup = benefitsGroups.find(
            (group) => String(group.id) === String(selectedGroupId),
        );

        if (selectedGroup?.title) {
            return `${translations?.category_search_placeholder ?? 'חיפוש '}${selectedGroup.title}`;
        }
    }

    return searchPlaceholderText
        ?? `${translations?.category_search_placeholder ?? 'חיפוש '}${pageTitle ?? 'הטבות הוטיותר'}`;
};

export const HotYoterBenefitsSection = ({
    benefitsGroups = [],
    searchPlaceholderText = null,
    relatedExpert = null,
    pageTitle = '',
    testID = 'hot-yoter-benefits-section',
}) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const translations = useSelector((store) => store.config.Translations);
    const categories = useSelector(
        (store) => cleanUpObject(store.config.categoriesArr).Data ?? [],
    );
    const deviceState = useSelector((store) => store.state.deviceState);

    const [selectedGroupId, setSelectedGroupId] = useState(HOT_YOTER_ALL_GROUPS_ID);
    const [selectedCategoryId, setSelectedCategoryId] = useState(HOT_YOTER_ALL_CATEGORIES_ID);
    const [searchInput, setSearchInput] = useState('');
    const [textQuery, setTextQuery] = useState('');
    const [benefitsPage, setBenefitsPage] = useState({ records: [], metadata: {} });
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const fetchRequestIdRef = useRef(0);
    const searchTimeoutRef = useRef(0);

    const searchParams = useMemo(
        () => buildHotYoterSearchParams({
            benefitsGroups,
            selectedGroupId,
            selectedCategoryId,
            textQuery,
        }),
        [benefitsGroups, selectedGroupId, selectedCategoryId, textQuery],
    );

    const hasBenefitGroups = searchParams.benefitGroupIds.length > 0;

    const filterKey = useMemo(
        () => JSON.stringify({
            selectedGroupId,
            selectedCategoryId,
            textQuery: searchParams.textQuery,
            benefitGroupIds: searchParams.benefitGroupIds,
        }),
        [searchParams, selectedCategoryId, selectedGroupId],
    );

    const fetchBenefits = useCallback(async (page, replace = false) => {
        const params = buildHotYoterSearchParams({
            benefitsGroups,
            selectedGroupId,
            selectedCategoryId,
            textQuery,
            page,
        });

        if (!params.benefitGroupIds.length) {
            setBenefitsPage({ records: [], metadata: {} });
            return;
        }

        const requestId = fetchRequestIdRef.current;

        if (page === 1) {
            setIsLoading(true);
        } else {
            setIsLoadingMore(true);
        }

        try {
            const { data } = await dispatch(
                searchApi.endpoints.searchHotYoterBenefits.initiate(params, {
                    forceRefetch: true,
                }),
            );

            if (requestId !== fetchRequestIdRef.current) {
                return;
            }

            const records = getHotYoterSearchRecords(data);
            const metadata = getHotYoterSearchMetadata(data);
            const activeBenefits = filterActiveHotYoterBenefits(records);

            setBenefitsPage((prev) => ({
                records: replace ? activeBenefits : [...prev.records, ...activeBenefits],
                metadata: replace ? metadata : {
                    ...metadata,
                    filters: prev.metadata?.filters ?? metadata.filters,
                },
            }));
        } finally {
            if (requestId === fetchRequestIdRef.current) {
                setIsLoading(false);
                setIsLoadingMore(false);
            }
        }
    }, [benefitsGroups, dispatch, selectedCategoryId, selectedGroupId, textQuery]);

    const fetchBenefitsRef = useRef(fetchBenefits);
    fetchBenefitsRef.current = fetchBenefits;

    useEffect(() => {
        if (!hasBenefitGroups) {
            setBenefitsPage({ records: [], metadata: {} });
            return;
        }

        fetchRequestIdRef.current += 1;
        setBenefitsPage({ records: [], metadata: {} });
        fetchBenefitsRef.current(1, true);
    }, [filterKey, hasBenefitGroups]);

    useEffect(() => () => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
    }, []);

    const handleLoadMore = useCallback(() => {
        if (isLoading || isLoadingMore) {
            return;
        }

        const nextPage = (benefitsPage.metadata?.currentPage ?? 1) + 1;
        fetchBenefits(nextPage, false);
    }, [benefitsPage.metadata, fetchBenefits, isLoading, isLoadingMore]);

    const benefits = benefitsPage.records;

    const applyTextQuery = useCallback((nextQuery) => {
        setTextQuery(normalizeHotYoterTextQuery(nextQuery));
    }, []);

    const availableCategories = useMemo(
        () => buildHotYoterAvailableCategories({
            categoryIds: getHotYoterCategoryFilterValues({
                benefits: { metadata: benefitsPage.metadata },
            }),
            categories,
            allCategoriesLabel: translations?.category_all_subcategories
                ?? translations?.['category_all_subcategories']
                ?? 'הכל',
        }),
        [benefitsPage.metadata, categories, translations],
    );

    const groupTags = useMemo(() => {
        if (benefitsGroups.length <= 1) {
            return [];
        }

        return benefitsGroups.map((group) => ({
            id: group.id,
            title: group.title,
        }));
    }, [benefitsGroups]);

    const isMobile = deviceState === 'mobile';
    const isMobileOrTablet = deviceState === 'mobile' || deviceState === 'tablet';
    const hasMultipleGroups = groupTags.length > 0;
    const hasCategorySlider = availableCategories.length > 1;
    const searchPlaceholder = getSearchPlaceholder(
        searchPlaceholderText,
        translations,
        selectedGroupId,
        benefitsGroups,
        pageTitle,
    );
    const isLoadingBenefits = hasBenefitGroups && isLoading && benefitsPage.records.length === 0;

    const handleSearchSubmit = (event) => {
        event.preventDefault();

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
            searchTimeoutRef.current = 0;
        }

        applyTextQuery(searchInput);
    };

    const handleSearchQueryChange = (event) => {
        const nextValue = event.target.value;

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        setSearchInput(nextValue);
        searchTimeoutRef.current = setTimeout(() => {
            applyTextQuery(nextValue);
        }, SEARCH_REQUEST_DELAY);
    };

    const handleClearSearch = () => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
            searchTimeoutRef.current = 0;
        }

        setSearchInput('');
        setTextQuery('');
    };

    const handleGroupSelect = (groupId) => {
        // Toggle off to show all benefits when the same group is clicked again.
        setSelectedGroupId((current) => (
            String(current) === String(groupId)
                ? HOT_YOTER_ALL_GROUPS_ID
                : groupId
        ));
        setSelectedCategoryId(HOT_YOTER_ALL_CATEGORIES_ID);
    };

    const handleGroupRemove = () => {
        setSelectedGroupId(HOT_YOTER_ALL_GROUPS_ID);
        setSelectedCategoryId(HOT_YOTER_ALL_CATEGORIES_ID);
    };

    const handleCategorySelect = (categoryId) => {
        setSelectedCategoryId(categoryId);
    };

    const handleCategoryRemove = () => {
        setSelectedCategoryId(HOT_YOTER_ALL_CATEGORIES_ID);
    };

    const categorySlider = hasCategorySlider ? (
        <div className="filters-categories hot-yoter-filters__categories">
            <CategorySlider
                selectedCategory={selectedCategoryId}
                availableCategories={availableCategories}
                onCategorySelect={handleCategorySelect}
                onCategoryRemove={handleCategoryRemove}
                customSliderSettings={{
                    ...CATEGORY_SLIDER_SETTINGS,
                    shared: {
                        ...CATEGORY_SLIDER_SETTINGS.shared,
                        nextArrow: isMobileOrTablet
                            ? <SliderArrow />
                            : <SliderArrow variant="circular" />,
                        prevArrow: isMobileOrTablet
                            ? <SliderArrow />
                            : <SliderArrow variant="circular" />,
                    },
                }}
            />
        </div>
    ) : null;

    const navigateToExpert = () => {
        const expertId = relatedExpert?.id ?? relatedExpert;

        if (expertId && String(expertId) !== '0') {
            history.push(`${routes.experts}?id=${expertId}`);
            return;
        }

        history.push(routes.experts);
    };

    const handleMagazineClick = () => {
        history.push(routes.magazine);
    };

    const searchForm = (
        <div
            className="hot-yoter-search-window search-window"
            testId={`${testID}-search-form`}
        >
            <form className="search-top" onSubmit={handleSearchSubmit}>
                <label className="input-label">
                    <input
                        type="text"
                        className="search-box"
                        placeholder={searchPlaceholder}
                        value={searchInput}
                        onChange={handleSearchQueryChange}
                        autoComplete="off"
                        aria-label={searchPlaceholder}
                        testId={`${testID}-search-input`}
                    />
                    {searchInput ? (
                        <img
                            src={getMediaPath(closeIcon)}
                            alt=""
                            className="clear-input"
                            onClick={handleClearSearch}
                            testId={`${testID}-search-clear`}
                        />
                    ) : null}
                </label>
                <button
                    type="submit"
                    className="submit"
                    testId={`${testID}-search-button`}
                    aria-label="חיפוש"
                >
                    <img
                        src={getMediaPath(searchIcon)}
                        alt=""
                        className="search-icon"
                    />
                </button>
            </form>
        </div>
    );

    const groupTagsRow = hasMultipleGroups ? (
        <HotYoterGroupTags
            groupTags={groupTags}
            selectedGroupId={selectedGroupId}
            onSelect={handleGroupSelect}
            onRemove={handleGroupRemove}
            testID={`${testID}-group-filter`}
        />
    ) : null;

    const categoryActions = (
        relatedExpert && <div className="category-actions hot-yoter-filters__actions">
            <button
                type="button"
                className="category-expert-button"
                onClick={navigateToExpert}
                testId={`${testID}-expert-button`}
            >
                <img
                    src={questionIcon}
                    alt="Question Icon"
                    className="category-expert-button-icon"
                />
                {translations?.category_expert_button ?? 'שאל את המומחה'}
            </button>

            <div className="actions-divider" />

            <button
                type="button"
                className="category-magazine-button"
                onClick={handleMagazineClick}
                testId={`${testID}-magazine-button`}
            >
                <img
                    src={tipsIcon}
                    alt="Tips Icon"
                    className="category-magazine-button-icon"
                />
                {(translations?.category_magazine_button ?? 'מרכז התוכן')}
            </button>
        </div>
    );

    return (
        <section className="hot-yoter-benefits-section" testId={testID}>
            <section className="category-filters-container hot-yoter-filters">
                {isMobile ? (
                    <>
                        <div className="hot-yoter-search-row hot-yoter-filters__search-row">
                            {searchForm}
                        </div>

                        {groupTagsRow ? (
                            <div className="filters-filter-row hot-yoter-filters__filter-row">
                                {groupTagsRow}
                            </div>
                        ) : null}

                        {hasCategorySlider ? (
                            <div className="filters-upper-row hot-yoter-filters__upper-row">
                                {categorySlider}
                            </div>
                        ) : null}

                        {categoryActions ? (
                            <div className="filters-lower-row hot-yoter-filters__lower-row">
                                {categoryActions}
                            </div>
                        ) : null}
                    </>
                ) : (
                    <>
                        {hasCategorySlider ? (
                            <div className="filters-upper-row hot-yoter-filters__upper-row">
                                {categorySlider}
                            </div>
                        ) : null}

                        <div
                            className={combineClassNames(
                                'filters-lower-row hot-yoter-filters__lower-row',
                                !hasCategorySlider && 'compact',
                            )}
                        >
                            <div className="hot-yoter-search-and-groups">
                                {searchForm}
                                {groupTagsRow}
                            </div>

                            {categoryActions}
                        </div>
                    </>
                )}
            </section>

            <section className="category-results-container" testId={`${testID}-results`}>
                {isLoadingBenefits ? (
                    <Loader className="benefits-loader" />
                ) : (
                    <HotYoterBenefitsResults
                        benefits={benefits}
                        metadata={benefitsPage.metadata}
                        translations={translations}
                        loadingMore={isLoadingMore}
                        onLoadMore={handleLoadMore}
                        testID={`${testID}-results-grid`}
                    />
                )}
            </section>
        </section>
    );
};
